import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import SocialLinks from "../../components/socialLinks/SocialLinks";
import PresentationsPage from "../presentationsPage/PresentationsPage";
import { useFirebase } from "../../firebase/userContext";
import { FEATURED_LINKS, PORTFOLIO_UID } from "../../config/siteContent";
import "./HomePage.css";

function getBookingLink(userData) {
  return (
    userData.bookingLink ||
    userData.calendly ||
    userData.calendarLink ||
    FEATURED_LINKS.booking ||
    (userData.email
      ? `mailto:${userData.email}?subject=Book%20time%20with%20me`
      : "")
  );
}

function getSubstackLink(userData) {
  return (
    userData.substack ||
    userData.substackLink ||
    userData.newsletterLink ||
    FEATURED_LINKS.substack ||
    ""
  );
}

function getCvLink(userData) {
  return userData.cvLink || userData.resumeLink || "";
}

function getCurrentProjectLink(userData) {
  return (
    userData.currentProjectLink || userData.projectLink || FEATURED_LINKS.currentProject || ""
  );
}

function getCredentialTitle(userData) {
  return (
    userData.credentialTitle ||
    userData.title ||
    "Doctor of Science (Automation Technology)"
  );
}

function getAdvisoryNote(userData) {
  return (
    userData.advisoryNote ||
    "Available for select advisory roles, research collaborations, and early-stage technical conversations."
  );
}

function getSection(searchParams) {
  const requestedSection = (searchParams.get("section") || "home").toLowerCase();

  if (requestedSection === "presentations") {
    return requestedSection;
  }

  return "home";
}

export default function HomePage() {
  const [userData, setUserData] = useState({});
  const [searchParams] = useSearchParams();
  const firebase = useFirebase();
  const section = getSection(searchParams);
  const bookingLink = getBookingLink(userData);
  const substackLink = getSubstackLink(userData);
  const cvLink = getCvLink(userData);
  const currentProjectLink = getCurrentProjectLink(userData);
  const credentialTitle = getCredentialTitle(userData);
  const advisoryNote = getAdvisoryNote(userData);

  useEffect(() => {
    firebase.getUsersBio(PORTFOLIO_UID, (data) => {
      setUserData(data || {});
    });
  }, [firebase]);

  return (
    <div className="home-page-shell">
      <Container className="home-page-layout">
        <Header />
        <div className="content-panel">
          {section === "home" ? (
            <section className="hero-grid">
              <div className="hero-copy">
                <h1>{userData.name || "Prashant Rai"}</h1>
                <p className="hero-credential">{credentialTitle}</p>
                <p className="hero-role">
                  {userData.position || "Researcher, builder, and communicator"}
                </p>
                <p className="hero-location">
                  <i className="bi bi-geo-alt-fill"></i>
                  <span>{userData.location || "Based globally"}</span>
                </p>
                <p className="hero-description">
                  {userData.description ||
                    "I work across research, communication, and product execution."}
                </p>
                <div className="hero-availability-card">
                  <div className="hero-availability-header">
                    <span className="hero-availability-kicker">Open to select advisory work</span>
                    {bookingLink ? (
                      <a
                        className="hero-availability-link"
                        href={bookingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Start a conversation
                      </a>
                    ) : null}
                  </div>
                  <p className="hero-availability-copy">{advisoryNote}</p>
                  <div className="hero-availability-tags" aria-label="Advisory focus areas">
                    <span>AI product strategy</span>
                    <span>Research translation</span>
                    <span>Technical communication</span>
                  </div>
                </div>
                <div className="hero-link-row hero-link-row-primary">
                  <div className="hero-actions">
                    {substackLink ? (
                      <a
                        className="hero-icon-action hero-icon-action-compact"
                        href={substackLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fas fa-newspaper"></i>
                        <span>Substack</span>
                      </a>
                    ) : (
                      <span className="hero-icon-action hero-icon-action-compact disabled-action">
                        <i className="fas fa-newspaper"></i>
                        <span>Substack soon</span>
                      </span>
                    )}
                    {bookingLink ? (
                      <a
                        className="hero-icon-action hero-icon-action-compact"
                        href={bookingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="bi bi-calendar-event-fill"></i>
                        <span>Book time</span>
                      </a>
                    ) : null}
                    {cvLink ? (
                      <a
                        className="hero-icon-action hero-icon-action-compact"
                        href={cvLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="bi bi-file-earmark-text-fill"></i>
                        <span>CV</span>
                      </a>
                    ) : null}
                    {currentProjectLink ? (
                      <a
                        className="hero-icon-action hero-icon-action-compact"
                        href={currentProjectLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="bi bi-rocket-takeoff-fill"></i>
                        <span>Project: SenCAN</span>
                      </a>
                    ) : null}
                  </div>
                </div>
                <div className="hero-social-row">
                  <SocialLinks userData={userData} compact className="hero-social-block" />
                </div>
              </div>
              <div className="hero-visual">
                <div className="hero-portrait-scene">
                  <span className="image-orb orb-one"></span>
                  <span className="image-orb orb-two"></span>
                  <img
                    className="hero-portrait"
                    src="/images/prashant1.JPG"
                    alt="Prashant Rai portrait"
                  />
                </div>
              </div>
            </section>
          ) : (
            <PresentationsPage uid={PORTFOLIO_UID} />
          )}
        </div>
      </Container>
      <Footer logo={userData.copyrightLogo || userData.profile_logo} />
    </div>
  );
}
